import { useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Color,
  Group,
  Mesh,
  PerspectiveCamera,
  ShaderMaterial,
  Texture,
  Uniform,
  Vector2,
} from 'three';

import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';
import { textureImage } from 'utils/textureImage';

import {
  scaleBackground,
  WEATHER_SCREEN_PREFIX,
} from '../WeatherScreens.utils';
import fragmentShader from './shader.frag';
import vertexShader from './shader.vert';

interface ThunderstormScreenProps extends GroupProps {
  backgroundTexture: Texture;
}

const ThunderstormScreen = forwardRef<Group, ThunderstormScreenProps>(
  ({ backgroundTexture, ...props }, ref) => {
    const screenRef = useRef<Group>(null!);
    const backgroundRef = useRef<Mesh>(null!);
    const randomnessTex = useTexture(publicAssets('/effects/random.jpeg'));

    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uRandomTexture: new Uniform(randomnessTex),
          uResolution: { value: new Vector2(1, 1) },
          uTime: { value: 0 },
          uBackground: new Uniform(backgroundTexture),
          uRainColor: { value: new Color(0x504f54) },
        },
      })
    );

    useFrame(({ camera, size, clock }) => {
      if (!screenRef.current || !backgroundRef.current || !backgroundTexture)
        return;

      scaleBackground({
        mesh: backgroundRef.current,
        camera: camera as PerspectiveCamera,
        size,
        texture: backgroundTexture,
        positionZ: screenRef.current.position.z,
      });

      materialRef.current.uniforms.uRandomTexture = new Uniform(randomnessTex);
      materialRef.current.uniforms.uBackground = new Uniform(backgroundTexture);
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uResolution.value.set(
        textureImage(backgroundTexture).width,
        textureImage(backgroundTexture).height
      );
    });

    useImperativeHandle(ref, () => screenRef.current);

    return (
      <group name={WEATHER_SCREEN_PREFIX + 'stormy'} ref={screenRef} {...props}>
        <mesh ref={backgroundRef} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[1, 1]} />
          <primitive object={materialRef.current} attach="material" />
        </mesh>
      </group>
    );
  }
);

ThunderstormScreen.displayName = 'ThunderstormScreen';
export default ThunderstormScreen;
